import {Checkbox, Tooltip} from '@mantine/core'
import {Cell, Row, TableNode} from '@table-library/react-table-library'
import React from 'react'
import {joinStrings} from '@appscience/utils'
import {DataTableSelectContext} from '../hooks/use-row-select'
import {DataTableColumnInfo} from '../model'
import {DataTableContentProps} from './content'

interface Props<ColumnID extends string, ITEM extends TableNode> extends DataTableContentProps<ColumnID, ITEM> {
  row: ITEM,
}

export function DataTableContentRow<ColumnID extends string, ITEM extends TableNode>({
  row,
  select,
  columns,
  getShortcutActions,
  interactiveRowsData,
  onRowDoubleClick,
}: Props<ColumnID, ITEM>) {
  const isInteractiveRow = interactiveRowsData?.checkRowInteractive && interactiveRowsData.checkRowInteractive(row)
  const content = (
    <Row item={row} onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row)} className={joinStrings(
      isInteractiveRow && 'interactive',
    )}>
      {select?.checkbox.show && <CheckboxCell select={select} row={row} /> }
      {<MemoizedRowContent columns={columns} row={row} />}
      {!!getShortcutActions && <Cell pinRight className='rowActions'>{getShortcutActions(row)}</Cell>}
    </Row>
  )

  if (isInteractiveRow && interactiveRowsData?.tooltipText) {
    return (
      <Tooltip
        label={interactiveRowsData.tooltipText}
        withArrow
        openDelay={300}
        closeDelay={100}
        placement='start'
        allowPointerEvents
        wrapLines
      >
        {content}
      </Tooltip>
    )
  }
  return content
}


interface CheckboxCellProps<ITEM extends TableNode> {
  select: DataTableSelectContext,
  row: ITEM
}

function CheckboxCell<ITEM extends TableNode>({
  select,
  row,
}: CheckboxCellProps<ITEM>) {
  return (
    <Cell pinLeft={select.checkbox.pinLeft} className='selectCell'>
      <Checkbox
        checked={select.state.ids.includes(row.id)}
        onChange={() => select.onToggle(row.id)}
      />
    </Cell>
  )
}


interface RowCellsProps<ColumnID extends string, ITEM extends TableNode> {
  columns: Array<DataTableColumnInfo<ColumnID, ITEM>>,
  row: ITEM,
}

function RowCells<ColumnID extends string, ITEM extends TableNode>({
  columns,
  row,
}: RowCellsProps<ColumnID, ITEM>) {
  return (
    <>
      {columns.map(column => (
        <Cell key={column.id} className={column.id}>{column.content(row)}</Cell>
      ))}
    </>
  )
}

const MemoizedRowContent = React.memo(RowCells) as typeof RowCells